import { useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { appConfirm } from "../../../components/ConfirmDialog";
import { Lock, PoundSterling, Rocket, Loader2 } from "lucide-react";

const gbp = (n) => "£" + Math.round(Number(n || 0)).toLocaleString("en-GB");

export function BookPanel({ quoteId, data, onBooked }) {
  const [busy, setBusy] = useState(null);

  const book = async (feeMode) => {
    const deferred = feeMode === "deferred";
    if (!(await appConfirm({
      title: deferred ? "Book this job — defer the £499?" : "Book this job — waive the £499?",
      description: deferred
        ? "Creates the booking WITHOUT taking payment. The £499 booking fee stays owed — it's added to the client's balance and flagged on the Money tab. You can email a payment link any time."
        : "Creates the booking and writes off the £499 booking fee entirely. Use only when agreed with the client.",
      confirmLabel: deferred ? "Book — £499 deferred" : "Book — £499 waived",
    }))) return;
    setBusy(feeMode);
    try {
      const r = await api.post(`/admin/quote/${encodeURIComponent(quoteId)}/book`, { fee_mode: feeMode });
      toast.success(r.data.already ? `Already booked — opening job ${r.data.reference}` : `Booked · job file ${r.data.reference}`);
      if (onBooked) onBooked(r.data.reference);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the booking"); }
    setBusy(null);
  };

  const promote = async () => {
    if (!(await appConfirm({
      title: "Confirm the job now — skip straight to Confirmed?",
      description: "Books the job AND marks it confirmed in one go (£499 waived). The file keeps its reference and all money features unlock.",
      confirmLabel: "Confirm job now",
    }))) return;
    setBusy("promote");
    try {
      const r = await api.post(`/admin/quote/${encodeURIComponent(quoteId)}/promote-to-job`);
      toast.success(r.data.already ? `Already promoted — opening ${r.data.reference}` : `Job ${r.data.reference} confirmed`);
      if (onBooked) onBooked(r.data.reference);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm the job"); }
    setBusy(null);
  };

  return (
    <div className="space-y-4" data-testid="jobfile-book-panel">
      <div className="border border-[#E5E7EB] bg-[#F8F6F2] p-4 flex items-start gap-3">
        <Lock size={16} className="text-[#6B7280] mt-0.5 shrink-0" />
        <div>
          <div className="text-sm font-bold text-[#12263A]">Money unlocks at booking</div>
          <p className="text-xs text-[#6B7280] mt-1">
            Invoicing and payments appear here once this quote becomes a booking — either the client pays the £499
            online, or you book it below without payment. The file keeps its reference <b>{data.reference}</b> throughout.
          </p>
        </div>
      </div>

      <div className="border border-[#E5E7EB] bg-white p-4">
        <div className="label-eyebrow mb-1">Quote total</div>
        <div className="text-3xl font-black text-[#12263A]" data-testid="book-panel-total">{gbp(data.total_inc_vat)}</div>
        <div className="text-xs text-[#6B7280] mt-0.5">Incl. VAT · £499 booking fee reserves the date &amp; survey</div>
      </div>

      <div className="border border-[#D8EED8] bg-[#F2F9F2] p-4">
        <div className="label-eyebrow text-[#1F6B3B] mb-1 flex items-center gap-1.5"><PoundSterling size={12} /> Book without payment</div>
        <p className="text-[11px] text-[#6B7280] mb-3">
          Client is proceeding but hasn&apos;t paid the £499 online. Book now and either <b>defer</b> the fee
          (it&apos;s added to their balance owed and flagged on the Money tab) or <b>waive</b> it entirely.
        </p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="book-deferred" onClick={() => book("deferred")} disabled={!!busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-[#1F6B3B] text-white hover:bg-[#155029] disabled:opacity-50">
            {busy === "deferred" ? <Loader2 size={13} className="animate-spin" /> : <PoundSterling size={13} />}
            Book — defer the £499 (added to balance)
          </button>
          <button data-testid="book-waived" onClick={() => book("waived")} disabled={!!busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-[#1F6B3B] text-[#1F6B3B] hover:bg-[#1F6B3B] hover:text-white disabled:opacity-50">
            {busy === "waived" ? <Loader2 size={13} className="animate-spin" /> : null}
            Book — waive the £499
          </button>
        </div>
        {!data.email && <p className="text-[11px] text-[#C0392B] mt-2" data-testid="book-no-email-warning">No email on file — add one via Edit contact (Overview) so payment links &amp; confirmations can be sent.</p>}
      </div>

      <div className="border border-[#DCE9F5] bg-[#F1F6FB] p-4">
        <div className="label-eyebrow text-[#12263A] mb-1 flex items-center gap-1.5"><Rocket size={12} /> Confirm job in one click</div>
        <p className="text-[11px] text-[#6B7280] mb-3">Skips the Booking &amp; Survey stages — books and confirms in one go (£499 waived).</p>
        <button data-testid="quote-detail-promote-to-job" onClick={promote} disabled={!!busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#12263A] text-white hover:bg-[#0d1b2a] disabled:opacity-50">
          {busy === "promote" ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />} Confirm job now
        </button>
      </div>
    </div>
  );
}
